/* eslint id-length: 0 */
import { parse, stringify } from 'qs';
import { push } from 'connected-react-router';
import { Map, OrderedSet } from 'immutable';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';

import * as api from './api';
import * as actions from './actions';
import * as constants from './constants';
import * as helpers from './helpers';
import * as selectors from './selectors';
import { utils as containerUtils } from './../container';

import { operations } from './index';

/**
 * Return archivesV2 api if experiment is enabled
 * @param {Boolean} isArchivesV2Enabled
 * @returns {Object}
 */
const getAPIProvider = (isArchivesV2Enabled) => (
  isArchivesV2Enabled ? api.archives : api.resto
);

/**
 * Update browser URL with query
 *
 * @return {Promise} - result of location.search update
 */
export const updateURLWithQuery = (payload) => (dispatch) => {
  const normalizedQuery = helpers.normalizeQueryInputValue(payload.q);
  const noEmptyValues = Object
    .entries({
      ...payload,
      q: helpers.unionQuery({ input: '', filters: normalizedQuery }),
    })
    .reduce((acc, [key, val]) => (val ? { ...acc, [key]: val } : acc), {});
  return dispatch(push({ search: stringify(noEmptyValues) }));
};

/**
 * Updates values of filters inside the store
 * @param {Array[]} payload - array of arrays of normalized filters
 *
 * @example
 * for param payload = [['text', ['foo']], ['name', ['bar']]]
 * should dispatch actions:
 * - remove all values for filters in store without any value
 * - for filter items with values - store those values
 * - if date filter changes - update it
 */
export const selectFiltersFromNormalizedPayload = (payload) => async (dispatch, getState) => {
  const filtersSequence = constants.FILTERS_MAP.concat(constants.XTM_FILTERS_MAP)
    .valueSeq().toArray().map((name) => name.toLowerCase());
  const filterNamesInPayload = payload.map(([name]) => name);
  const filtersNotIncludedInPayload = filtersSequence
    .filter((filterName) => !filterNamesInPayload.includes(filterName));

  const filters = getState().apps.archives.query.get('filters');

  payload
    // we want to update values only for valid filter
    .filter(([name]) => filtersSequence.includes(name))
    .forEach(([name, values]) => {
      let normalizedValues = values;
      if (name === constants.TEAM_FILTER) {
        normalizedValues = containerUtils.tryMapLabelsToIds({
          items: values,
          labels: filters.getIn([constants.TEAM_FILTER, 'labels']),
        });
      }

      normalizedValues = Array.isArray(normalizedValues) ? normalizedValues : [normalizedValues];
      dispatch(actions.updateFacetValue({
        name,
        data: {
          items: OrderedSet(normalizedValues),
        },
      }));
    });

  payload
    .filter(([name, values]) => (
      name === constants.DATE_FILTER &&
      values?.length === 2
    ))
    // eslint-disable-next-line no-unused-vars
    .forEach(([name, values]) => {
      dispatch(operations.updateDateFacet({
        startDate: helpers.parseDate(values[0]),
        endDate: helpers.parseDate(values[1]),
      }));
    });

  filtersNotIncludedInPayload
    .forEach((name) => {
      dispatch(actions.updateFacetValue({
        name, data: { items: OrderedSet() },
      }));
    });
};

/**
 * Call api for new archives results
 * @param {Object} query
 * @param {string} query.payload - final query which should be sent to the backend
 * @param {boolean} query.mergeResults - concat or replace previous results?
 *
 * @return {Promise} result of API call
 */
export const search = ({ isArchivesV2Enabled } = {}) => (dispatch, getState) => {
  const { urlQueryString, searchQueryParam } = selectors.searchQueryParamSelector(getState());
  const apiProvider = getAPIProvider(isArchivesV2Enabled);
  Promise.all([
    dispatch(updateURLWithQuery(urlQueryString)),
    dispatch(actions.searchRequest()),
  ]);
  return apiProvider.searchV11(searchQueryParam)
    .then(({ data }) => (
      dispatch(actions.searchSuccess(data))
    )).catch((error) => {
      dispatch(actions.searchFailure(error, { skipErrorPage: true }));
      if (error?.response?.status !== 400 && error?.response?.status !== 404) {
        const message = (error?.response?.data) ? error.response.data.message : error.message;
        dispatch(notificationActions.notifyDanger(
          message, 'We\'ve experienced an issue while loading archives.',
        ));
      }
    });
};

/**
 * Fetch values for filters from API
 * @param {Object} query
 * @param {string} query.name - name of the facet which should be fetched
 *
 * @return {Promise} - result of API call
 *
 * @example
 * for param payload = { name: 'builds'}
 * should fetch into the store possible values for builds filter
 */
export const fetchFacet = ({ name, payload }, isArchivesV2Enabled) => (dispatch, getState) => {
  if (name === constants.STATUS_FILTER) {
    return Promise.resolve();
  }
  const apiProvider = getAPIProvider(isArchivesV2Enabled);
  const filterDate = getState().apps.archives.query.get('filterDate');
  let q = `date: ${helpers.buildDateRange(filterDate.toArray())}`;
  if (payload) {
    q += ` AND ${name}: ${payload}`;
  }
  const parsed = parse({ q });

  dispatch(actions.fetchFacetRequest({ name }));
  return apiProvider.fetchFacet({ name, params: parsed }).then(
    ({ data }) => dispatch(actions.fetchFacetSuccess({ facetName: name, ...data })),
    (error) => {
      dispatch(actions.fetchFacetFailure({ name }));
      throw Error(error);
    },
  );
};

/**
 * Update date filter
 * @param {Object} dateRange
 * @param {Date} dateRange.startDate
 * @param {Date} dateRange.endDate
 * @returns {Promise}
 */
export const updateDateFacet = ({ startDate, endDate }) => (dispatch) => {
  if (!helpers.isValidDateRange({ startDate, endDate })) {
    return Promise.resolve();
  }

  return dispatch(actions.updateDateFacet(Map({ startDate, endDate })));
};

/**
 * Called when filter from dropdown value is changed.
 * Should update filters, build the query and ask backend for updated data.
 * @return {Promise} - result of API call for new archive results
 */
export const updateFacets = ({
  inputQueryRef,
  payload,
  isArchivesV2Enabled,
}) => async (dispatch, getState) => {
  if (payload.name === constants.DATE_FILTER) {
    await dispatch(operations.updateDateFacet(helpers.fullDayRange(
      payload.data.get('startDate'),
      payload.data.get('endDate'),
    )));
  } else {
    await dispatch(actions.updateFacetValue(payload));
  }

  await dispatch(actions.resetPage());
  const mergedInputQuery = selectors.buildFullQuerySelector(getState());
  inputQueryRef.updateValue(mergedInputQuery);
  return dispatch(search({ isArchivesV2Enabled }));
};

/**
 * Called when query input is saved.
 * Should parse updated query input, select filters based on that change, build the query
 * and ask backend for updated data.
 * @param {Object} query
 * @param {Object} query.inputQueryRef - reference to react component of InputQuery
 * @param {string} query.payload - query string
 * }}
 * @return {Promise} - result of API call for new archive results
 */
export const updateViaQueryInput = ({
  inputQueryRef,
  payload,
  isArchivesV2Enabled,
}) => async (dispatch, getState) => {
  await dispatch(actions.resetPage());
  await dispatch(actions.updateQuery(payload));
  const parsedQuery = helpers.normalizeQueryInputValue(payload);
  await dispatch(selectFiltersFromNormalizedPayload(parsedQuery));
  const query = selectors.buildFullQuerySelector(getState());
  inputQueryRef.updateValue(query);
  return dispatch(search({ isArchivesV2Enabled }));
};

/**
 * Save selected sort type
 * and ask backend for updated data.
 *
 * @return {Promise} - result of API call for new archive results
 */
export const saveSortBy = (payload, isArchivesV2Enabled) => async (dispatch) => {
  await dispatch(actions.resetPage());
  await dispatch(actions.updateSort(payload));
  return dispatch(search({ isArchivesV2Enabled }));
};

/**
 * Build store values based on query string from URL
 *
 * @return {Promise} - result of updating input query action
 */
export const getQueryFromURL = ({ isArchivesV2Enabled } = {}) => (dispatch, getState) => {
  const params = selectors.queryFromURLSelector(getState());
  if (!params) {
    return Promise.resolve();
  }
  return Promise.all([
    dispatch(selectFiltersFromNormalizedPayload(params.queryWithLabels)),
    dispatch(actions.updateQuery(params.queryString)),
    dispatch(actions.changePage(params.page, isArchivesV2Enabled)),
  ]);
};

export const changePage = (payload, isArchivesV2Enabled) => async (dispatch) => {
  await dispatch(actions.changePage(payload));
  return dispatch(operations.search({ isArchivesV2Enabled }));
};
